// Five review layouts, from the review kit ("The good word"): the editorial
// quote, the review receipt, the conversation, the rating poster and the
// thank-you note. The review is always their words, word for word.
import { flow, textBlock, brand, footer, rule, display, label, body, strong } from '../../kit'
import { dryPainter } from '../../painter'
import { base, ground, serif, serifFont, mono, heavy, triangle, starsOf } from './common'

const joined = (c) => [String(c.name || '').trim(), String(c.service || '').trim()].filter(Boolean).join(' / ')

export default {
  // A big quotation mark, the review in a serif, a short rule by their name
  quote(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const n = starsOf(c)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock('The word around the studio', () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60 }),
      textBlock(c.headline, s => ({ ...display(env, ink, 78 * s), x: fr.left - 3, w: w + 3, maxLines: 2 }), { gap: 20, slot: 'headline' }),
      {
        gap: 34,
        flex: 1,
        draw: (pp, y, s) => {
          const size = Math.round(380 * s)
          pp.text('“', { font: serifFont(env), weight: 400, size, lh: 1, color: k.accent, x: fr.left - 22, y: y - Math.round(size * 0.14), w: 300, maxLines: 1, minSize: size })
          return y + Math.round(size * 0.3)
        },
      },
      textBlock(c.quote, s => ({ ...serif(env, k.ink, 70 * s), x: fr.left, w: w - 24, maxLines: 7, maxH: 460 * s, minSize: 36, lh: 1.13 }), { gap: 24, slot: 'quote' }),
      {
        gap: 40,
        flex: 1,
        draw: (pp, y) => {
          if (!c.name && !c.service) return y
          pp.line(fr.left, y + 22, fr.left + 104, y + 22, k.accent, 2)
          const nm = c.name ? pp.text(c.name, { ...strong(env, ink, 34), x: fr.left + 130, y, w: w - 130, maxLines: 1, slot: 'name' }) : { bottom: y }
          return c.service ? pp.text(c.service, { ...body(env, ink, 32), x: fr.left + 130, y: nm.bottom + 4, w: w - 130, maxLines: 1, slot: 'service' }).bottom : nm.bottom
        },
      },
      n ? { gap: 26, draw: (pp, y) => { pp.stars(fr.left, y, 34, n, k.accent, 6); return y + 34 } } : null,
      footer(env, fr, ink),
    ])
  },

  // Printed like a till receipt: typewriter words, dashed rules, torn foot
  receipt(p, env) {
    const { fr, c, k, w } = base(env)
    const on = ground(p, env, true)
    const n = starsOf(c)
    const x = fr.left + 20
    const rw = w - 40
    const pad = 42
    const inner = rw - pad * 2
    const teeth = 17
    const content = (q, top, s) => {
      const dash = (yy) => q.line(x + pad, yy, x + rw - pad, yy, k.ink, 2, [10, 10])
      let y = q.text('One visit. A lasting impression.', { ...mono(k.ink, 30), weight: 700, upper: true, x: x + pad, y: top, w: inner, maxLines: 2 }).bottom
      dash(y + 22)
      y += 44
      if (c.service) y = q.text(c.service, { ...mono(k.ink, 36), weight: 700, upper: true, x: x + pad, y: y + 8, w: inner, maxLines: 2, slot: 'service' }).bottom
      if (n) { q.stars(x + pad, y + 26, Math.round(52 * s), n, k.accent, 6); y += 26 + Math.round(52 * s) }
      return q.text(c.quote || '', { ...mono(k.ink, Math.round(42 * s)), lh: 1.2, x: x + pad, y: y + 32, w: inner, maxLines: 9, maxH: 330 * s, slot: 'quote' }).bottom
    }
    // The dashed rule, their name and the rating, from where the slip ends
    const foot = (q, y) => {
      q.line(x + pad, y, x + rw - pad, y, k.ink, 2, [10, 10])
      const ny = y + 28
      const r = q.text(n ? `${n}/5` : 'Review', { ...mono(k.ink, 44), weight: 700, x: x + pad, y: ny - 4, w: inner, align: 'right', maxLines: 1 })
      const nm = q.text(c.name || '', { ...mono(k.ink, 36), weight: 700, x: x + pad, y: ny, w: inner - r.width - 30, maxLines: 1, slot: 'name' })
      return Math.max(nm.bottom, r.bottom)
    }
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(c.headline, s => ({ ...display(env, on, 88 * s), x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 58, slot: 'headline' }),
      {
        gap: 48,
        draw: (pp, y, s) => {
          const dp = pp.isDry ? pp : dryPainter(pp)
          const footH = foot(dp, 0) + pad + 20 + teeth
          const h = Math.max(content(dp, y + pad, s) + 34 - y + footH, Math.round(780 * s))
          pp.rect(x, y, rw, h, k.paper)
          for (let tx = x; tx < x + rw - 1; tx += 32) triangle(pp, [[tx, y + h + 0.5], [tx + 16, y + h - teeth], [Math.min(tx + 32, x + rw), y + h + 0.5]], k.accent)
          content(pp, y + pad, s)
          foot(pp, y + h - footH)
          return y + h
        },
      },
      footer(env, fr, on),
    ])
  },

  // Their words in a speech bubble, your reply underneath
  conversation(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const n = starsOf(c)
    const pad = 48
    const tailH = 70
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.headline, s => ({ ...display(env, ink, 120 * s), x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 52, slot: 'headline' }),
      {
        gap: 56,
        flex: 1,
        draw: (pp, y, s) => {
          const inside = (q, top) => {
            let yy = top
            if (n) { q.stars(fr.left + pad, yy, Math.round(44 * s), n, k.on, 6); yy += Math.round(44 * s) + 30 }
            return q.text(c.quote || '', { ...body(env, k.on, Math.round(56 * s)), lh: 1.17, x: fr.left + pad, y: yy, w: w - pad * 2, maxLines: 7, maxH: 400 * s, slot: 'quote' }).bottom
          }
          const h = inside(pp.isDry ? pp : dryPainter(pp), y + pad) - y + pad
          pp.rect(fr.left, y, w, h, k.accent, 48)
          triangle(pp, [[fr.left + 86, y + h - 8], [fr.left + 86, y + h + tailH], [fr.left + 176, y + h - 8]], k.accent)
          inside(pp, y + pad)
          return y + h + tailH
        },
      },
      textBlock(joined(c), () => ({ ...strong(env, ink, 32), x: fr.left + 212, w: w - 212, maxLines: 2 }), { gap: -52, slot: 'name' }),
      textBlock(c.reply, s => ({ ...serif(env, ink, 38 * s), x: fr.left, w, maxLines: 2 }), { gap: 58, flex: 2, slot: 'reply' }),
      footer(env, fr, ink),
    ])
  },

  // The stars they gave, as big as they go, over their words
  rating(p, env) {
    const { fr, c, w } = base(env)
    const on = ground(p, env, true)
    const n = starsOf(c)
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(c.headline, s => ({ ...display(env, on, 86 * s), x: fr.left - 3, w: w + 3, maxLines: 2 }), { gap: 58, slot: 'headline' }),
      n ? {
        gap: 34,
        draw: (pp, y, s) => {
          const big = Math.round(330 * s)
          const num = pp.text(String(n), { ...heavy(on, big), x: fr.left - 14, y, w: 420, maxLines: 1, minSize: big })
          const out = pp.text('/ 5', { ...strong(env, on, Math.round(116 * s)), x: fr.left + num.width + 10, y: num.bottom - Math.round(150 * s), w: 280, maxLines: 1 })
          const col = fr.left + Math.round(610 * s)
          pp.stars(col, y + Math.round(58 * s), Math.round(48 * s), n, on, 6)
          pp.text('One client’s experience', { ...label(env, on), x: col, y: y + Math.round(140 * s), w: fr.right - col, maxLines: 2 })
          return Math.max(num.bottom, out.bottom)
        },
      } : textBlock('Good words.', s => ({ ...display(env, on, 160 * s), upper: true, x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 40 }),
      rule(fr, on, { gap: 44 }),
      textBlock(c.quote, s => ({ ...strong(env, on, Math.round(58 * s)), lh: 1.13, x: fr.left, w, maxLines: 7, maxH: 380 * s }), { gap: 48, slot: 'quote' }),
      textBlock(joined(c), () => ({ ...body(env, on, 34), x: fr.left, w, maxLines: 2 }), { gap: 44, flex: 1, slot: 'name' }),
      footer(env, fr, on),
    ])
  },

  // A note pinned up on the wall: slightly turned, with a strip of tape
  note(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const n = starsOf(c)
    const cw = w - 20
    const pad = 54
    const turn = -0.045
    const words = (q, x, top, s, slots) => {
      let y = top
      if (n) { q.stars(x, y, Math.round(42 * s), n, k.ink, 6); y += Math.round(42 * s) + 34 }
      const qt = q.text(c.quote || '', { ...serif(env, k.ink, Math.round(60 * s)), lh: 1.16, x, y, w: cw - pad * 2, maxLines: 7, maxH: 400 * s })
      if (slots) slots.push({ slot: 'quote', y: qt.top, h: qt.bottom - qt.top })
      y = qt.bottom
      if (c.name) {
        const nm = q.text(c.name, { ...strong(env, k.ink, 34), x, y: y + 40, w: cw - pad * 2, maxLines: 1 })
        if (slots) slots.push({ slot: 'name', y: nm.top, h: nm.bottom - nm.top })
        y = nm.bottom
      }
      if (c.service) {
        const sv = q.text(c.service, { ...body(env, k.ink, 32), x, y: y + 6, w: cw - pad * 2, maxLines: 1 })
        if (slots) slots.push({ slot: 'service', y: sv.top, h: sv.bottom - sv.top })
        y = sv.bottom
      }
      return y
    }
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.headline, s => ({ ...display(env, ink, 112 * s), x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 52, slot: 'headline' }),
      {
        gap: 90,
        flex: 1,
        draw: (pp, y, s) => {
          const ch = words(pp.isDry ? pp : dryPainter(pp), 0, 0, s) + pad * 2
          const lean = Math.round(Math.abs(Math.sin(turn)) * cw / 2)
          if (pp.isDry) return y + ch + lean * 2
          const cx = fr.W / 2
          const cy = y + lean + ch / 2
          const { ctx } = pp
          ctx.save()
          ctx.translate(cx, cy)
          ctx.rotate(turn)
          pp.rect(-cw / 2, -ch / 2, cw, ch, k.pop)
          pp.rect(-121, -ch / 2 - 30, 242, 62, k.accent)
          const slots = []
          words(pp, -cw / 2 + pad, -ch / 2 + pad, s, slots)
          ctx.restore()
          // Tap targets as the note would sit unturned
          slots.forEach(b => pp.boxes.push({ slot: b.slot, x: cx - cw / 2 + pad, y: cy + b.y, w: cw - pad * 2, h: Math.max(b.h, 40) }))
          return y + ch + lean * 2
        },
      },
      { gap: 0, flex: 1, draw: (pp, y) => y },
      footer(env, fr, ink),
    ])
  },
}
